import {
  BookOpen,
  Clock3,
  Film,
  LoaderCircle,
  TriangleAlert,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import type { LabJob } from "./api";
import { isActiveLabStatus } from "./api";
import { resolveLabProviderIdentity } from "./providerIdentity";

const relativeTime = (value: string) => {
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return new Date(value).toLocaleDateString();
};

const statusVariant = (status: LabJob["status"]) => {
  if (status === "completed") return "default" as const;
  if (["failed", "submit_failed", "attention_required"].includes(status)) {
    return "destructive" as const;
  }
  return "secondary" as const;
};

export function LabHistory({
  jobs,
  selectedId,
  onSelect,
  isLoading,
}: {
  jobs: LabJob[];
  selectedId: string | null;
  onSelect: (jobId: string) => void;
  isLoading: boolean;
}) {
  return (
    <Card className="h-full min-h-0">
      <Tabs defaultValue="history" className="h-full">
        <CardHeader className="pb-3">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="history" className="gap-2">
              History
              {isLoading ? (
                <LoaderCircle className="size-3.5 animate-spin" />
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="how" className="gap-2">
              <BookOpen className="size-3.5" /> How it works
            </TabsTrigger>
          </TabsList>
        </CardHeader>
        <TabsContent value="history" className="mt-0">
          <CardContent className="max-h-[70vh] space-y-2 overflow-y-auto">
            {!isLoading && jobs.length === 0 ? (
              <div className="grid min-h-48 place-items-center text-center">
                <div>
                  <Film className="mx-auto mb-2 size-6 text-muted-foreground" />
                  <p className="text-sm font-medium">No Labs runs yet</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your persistent generations will appear here.
                  </p>
                </div>
              </div>
            ) : null}
            {jobs.map((job) => {
              const identity = resolveLabProviderIdentity(job);
              return (
                <Button
                  key={job.job_id}
                  variant="ghost"
                  onClick={() => onSelect(job.job_id)}
                  className={cn(
                    "h-auto w-full items-start justify-start gap-3 whitespace-normal border p-3 text-left",
                    selectedId === job.job_id && "border-foreground bg-muted",
                  )}
                >
                  <span className="relative grid size-11 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                    {job.thumbnail_url ? (
                      <img
                        src={job.thumbnail_url}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : isActiveLabStatus(job.status) ? (
                      <LoaderCircle className="size-4 animate-spin" />
                    ) : [
                        "failed",
                        "submit_failed",
                        "attention_required",
                      ].includes(job.status) ? (
                      <TriangleAlert className="size-4 text-destructive" />
                    ) : (
                      <Film className="size-4" />
                    )}
                    {job.thumbnail_url && isActiveLabStatus(job.status) ? (
                      <span className="absolute inset-0 grid place-items-center bg-black/50 text-white">
                        <LoaderCircle className="size-4 animate-spin" />
                      </span>
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">
                        {identity.label}
                      </span>
                      <Badge variant={statusVariant(job.status)}>
                        {job.status}
                      </Badge>
                    </span>
                    <span className="mt-1 block truncate font-mono text-[11px] text-muted-foreground">
                      {job.model_id || "deployed default"}
                    </span>
                    <span className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
                      <Clock3 className="size-3" />
                      {relativeTime(job.created_at)} · {job.duration_seconds}s ·{" "}
                      {job.aspect_ratio || "native"}
                    </span>
                  </span>
                </Button>
              );
            })}
          </CardContent>
        </TabsContent>
        <TabsContent value="how" className="mt-0">
          <CardContent className="space-y-4 text-sm">
            <div>
              <p className="font-medium">Persistent by design</p>
              <p className="mt-1 text-muted-foreground">
                Inputs, immutable settings, status, and output survive refreshes
                and CRM deploys.
              </p>
            </div>
            <div>
              <p className="font-medium">Exact provider experiments</p>
              <p className="mt-1 text-muted-foreground">
                Each run records the concrete provider and deployed model from
                the shared registry. Unsupported controls are rejected before a
                paid request.
              </p>
            </div>
            <div>
              <p className="font-medium">Isolated from Echo delivery</p>
              <p className="mt-1 text-muted-foreground">
                Labs uses dedicated storage and an isolated Batch entrypoint. It
                does not alter WhatsApp B2C/B2B routes or send a user message.
              </p>
            </div>
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
